/**
 * Nolana theme — product page behaviour.
 * Ported from tema-demo/assets/js/nolana.js "Ürün detay" and extended for Shopify variants.
 *
 * - Variant picker: option inputs ([data-option-input]) resolve the matching variant from the
 *   embedded product JSON ([data-product-json]); price, compare-at price, availability, the
 *   hidden variant id, the URL (?variant=) and the active gallery media are updated.
 * - Sticky mobile buy bar ([data-buybar]) appears once the buy row scrolls out of view and
 *   mirrors the current variant and quantity.
 * - Add-to-cart submit is handled by assets/nolana-product-form.js (form[data-product-form]);
 *   this module only reflects Cart API errors into [data-form-error].
 */
(function () {
  "use strict";

  if (document.documentElement.dataset.nolanaProductReady) return;
  document.documentElement.dataset.nolanaProductReady = "true";

  function strings() {
    return (window.NolanaConfig && window.NolanaConfig.strings) || {};
  }

  function formatMoney(cents) {
    if (window.Nolana && typeof window.Nolana.formatMoney === "function") return window.Nolana.formatMoney(cents);
    return (cents / 100).toFixed(2);
  }

  function setupProduct(root) {
    if (!root || root.dataset.productBound === "true") return;
    root.dataset.productBound = "true";

    // [data-product-json] carries product.variants only (smaller payload, no description HTML).
    var jsonNode = root.querySelector("[data-product-json]");
    var product = null;
    try {
      var parsed = jsonNode ? JSON.parse(jsonNode.textContent) : null;
      product = Array.isArray(parsed) ? { variants: parsed } : parsed;
    } catch (error) {
      product = null;
    }

    var form = root.querySelector("form[data-product-form]");
    var variantInput = root.querySelector("[data-variant-input]");
    var priceNode = root.querySelector("[data-price]");
    var compareNode = root.querySelector("[data-compare-price]");
    var submit = root.querySelector("[data-add-submit]");
    var submitLabel = root.querySelector("[data-add-label]");
    var errorNode = root.querySelector("[data-form-error]");
    var gallery = root.querySelector("[data-gallery]");
    var buybar = document.querySelector("[data-buybar]");
    var buyRow = root.querySelector(".pdp__buy");
    var quantityInput = root.querySelector("[data-qty-input]");
    var labels = {
      add: (submit && submit.getAttribute("data-label-add")) || strings().addToCart || (submitLabel ? submitLabel.textContent.trim() : ""),
      soldOut: (submit && submit.getAttribute("data-label-sold-out")) || strings().soldOut || "",
      unavailable: (submit && submit.getAttribute("data-label-unavailable")) || strings().unavailable || "",
    };

    function currentVariant() {
      if (!product || !product.variants) return null;
      var id = variantInput ? parseInt(variantInput.value, 10) : NaN;
      return product.variants.find(function (variant) {
        return variant.id === id;
      }) || null;
    }

    function selectedOptions() {
      var inputs = root.querySelectorAll("[data-option-input]:checked, select[data-option-input]");
      var values = [];
      inputs.forEach(function (input) {
        var position = parseInt(input.getAttribute("data-option-position"), 10) - 1;
        values[position] = input.value;
      });
      return values;
    }

    function findVariant(options) {
      if (!product || !product.variants) return null;
      return product.variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return options[index] === undefined || options[index] === value;
        });
      }) || null;
    }

    function updateAvailabilityPills(options) {
      root.querySelectorAll("[data-option-input]").forEach(function (input) {
        var position = parseInt(input.getAttribute("data-option-position"), 10) - 1;
        var trial = options.slice();
        trial[position] = input.value;
        var match = product.variants.find(function (variant) {
          return variant.options.every(function (value, index) {
            return trial[index] === undefined || trial[index] === value;
          });
        });
        var pill = input.closest(".variant-pill");
        var unavailable = !match || !match.available;
        if (pill) pill.classList.toggle("is-unavailable", unavailable);
        input.setAttribute("aria-disabled", unavailable ? "true" : "false");
        var note = pill ? pill.querySelector("[data-unavailable-text]") : null;
        if (note) note.hidden = !unavailable;
      });
    }

    /** Mirrors the checked radio into a class for browsers without :has() support. */
    function syncPillState() {
      document.querySelectorAll(".variant-pill").forEach(function (pill) {
        var radio = pill.querySelector("[data-option-input]");
        pill.classList.toggle("is-selected", !!(radio && radio.checked));
      });
    }

    function scrollGalleryTo(mediaId) {
      if (!gallery || !mediaId) return;
      var figure = gallery.querySelector('[data-media-id="' + mediaId + '"]');
      if (!figure) return;
      gallery.querySelectorAll("[data-media-active]").forEach(function (el) {
        el.removeAttribute("data-media-active");
      });
      figure.setAttribute("data-media-active", "");
      var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      figure.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "nearest", inline: "center" });
    }

    function updateSelectedLabels() {
      root.querySelectorAll("[data-option-input]:checked").forEach(function (input) {
        var label = root.querySelector('[data-option-selected="' + input.getAttribute("data-option-position") + '"]');
        if (label) label.textContent = input.value;
      });
    }

    function applyVariant(variant) {
      updateSelectedLabels();
      if (!variant) {
        if (submit) submit.disabled = true;
        if (submitLabel) submitLabel.textContent = labels.unavailable;
        if (variantInput) {
          variantInput.disabled = true;
          variantInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
        updateBuybar(null);
        return;
      }
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.disabled = !variant.available;
        // Dynamic checkout / installments components listen for changes on input[name="id"].
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (priceNode) priceNode.textContent = formatMoney(variant.price);
      if (compareNode) {
        var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareNode.hidden = !hasCompare;
        if (hasCompare) compareNode.textContent = formatMoney(variant.compare_at_price);
      }
      if (submit) submit.disabled = !variant.available;
      if (submitLabel) submitLabel.textContent = variant.available ? labels.add : labels.soldOut;
      if (variant.featured_media && variant.featured_media.id) scrollGalleryTo(variant.featured_media.id);
      updateBuybar(variant);

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      }
      root.dispatchEvent(new CustomEvent("nolana:variant:change", { detail: { variant: variant }, bubbles: true }));
    }

    function updateBuybar(variant) {
      if (!buybar) return;
      var price = buybar.querySelector("[data-buybar-price]");
      var button = buybar.querySelector("[data-add-to-cart]");
      if (!variant) {
        if (button) {
          button.disabled = true;
          button.removeAttribute("data-variant-id");
        }
        return;
      }
      if (price) price.textContent = formatMoney(variant.price);
      if (button) {
        button.setAttribute("data-variant-id", variant.id);
        button.disabled = !variant.available;
      }
    }

    root.addEventListener("change", function (event) {
      if (!event.target.closest("[data-option-input]")) return;
      var options = selectedOptions();
      var variant = findVariant(options);
      updateAvailabilityPills(options);
      syncPillState();
      applyVariant(variant);
    });


    /* Sticky mobile buy bar */
    if (buybar && buyRow && "IntersectionObserver" in window) {
      new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          buybar.classList.toggle("is-show", !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      ).observe(buyRow);
      if (quantityInput) {
        var buybarButton = buybar.querySelector("[data-add-to-cart]");
        if (buybarButton) buybarButton.setAttribute("data-quantity-from", "#" + quantityInput.id);
      }
    }

    var initial = currentVariant();
    if (initial) updateBuybar(initial);
    if (product && product.variants && product.variants.length > 1) updateAvailabilityPills(selectedOptions());
  }

  /* Cart API errors from nolana-product-form.js — resolved at event time so Theme Editor reloads never stack listeners. */
  document.addEventListener("nolana:cart:error", function (event) {
    var errorNode = document.querySelector("[data-product-page] [data-form-error]");
    if (!errorNode) return;
    var error = event.detail && event.detail.error;
    var message = error && error.data ? error.message : strings().cartError || "";
    if (!message) return;
    errorNode.textContent = message;
    errorNode.hidden = false;
  });
  document.addEventListener("nolana:cart:updated", function () {
    var errorNode = document.querySelector("[data-product-page] [data-form-error]");
    if (errorNode) errorNode.hidden = true;
  });

  /* Focus ring on the pill that wraps a hidden radio (browsers without :has()). */
  document.addEventListener("focusin", function (event) {
    var pill = event.target.closest ? event.target.closest(".variant-pill") : null;
    if (pill) pill.classList.add("is-focused");
  });
  document.addEventListener("focusout", function (event) {
    var pill = event.target.closest ? event.target.closest(".variant-pill") : null;
    if (pill) pill.classList.remove("is-focused");
  });

  function init(scope) {
    (scope || document).querySelectorAll("[data-product-page]").forEach(setupProduct);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
